import { getSharedDictionary } from '@gn-audio/jss-core';

import { tailwindConfig } from '../tailwind-presets';
import { ColorHexMapping } from '../types/colorHexMapping';

const { colors } = tailwindConfig.theme;

export type HexColor = `#${string}`;
export type TailwindColor = keyof typeof colors;
export type TailwindColorScale = typeof colors.beige;
export type TailwindColorVariation = keyof typeof colors.beige;

/**
 * Converts a hexadecimal color string to an array of RGB values.
 *
 * @param {HexColor} hex - The hexadecimal color string (e.g., "#RRGGBB").
 * @returns {number[]} An array containing the red, green, and blue values as numbers.
 */
const hexToRgbArray = (hex: HexColor): number[] => {
  const hexadecimalBase = 16;
  const redHexPart = hex.slice(1, 3);
  const greenHexPart = hex.slice(3, 5);
  const blueHexPart = hex.slice(5, 7);

  const red = parseInt(redHexPart, hexadecimalBase);
  const green = parseInt(greenHexPart, hexadecimalBase);
  const blue = parseInt(blueHexPart, hexadecimalBase);

  return [red, green, blue];
};

/**
 * Determines if a given hex color is bright.
 *
 * @param {HexColor} [colorHex] - The hex color code to evaluate. If not provided, defaults to true.
 * @returns {boolean} Returns true if the color is bright, otherwise false.
 */
export const isColorBright = (colorHex?: HexColor): boolean => {
  if (!colorHex) return true;
  const colorRGB: number[] = hexToRgbArray(colorHex);
  const brightness = Math.round((colorRGB[0] * 299 + colorRGB[1] * 587 + colorRGB[2] * 114) / 1000);
  return brightness > 125;
};

export const getTailwindColor = (color: string): HexColor | undefined => {
  if (!color) return;

  const splitColors = color.split(/[.-]/g);
  const [colorName, variation] = splitColors as [TailwindColor, TailwindColorVariation];

  if (splitColors.length === 1) {
    return (
      (((colors[colorName] as TailwindColorScale)?.DEFAULT || colors[colorName]) as HexColor) ||
      undefined
    );
  }

  if (variation) {
    const color = (colors[colorName] as TailwindColorScale)?.[variation];

    if (typeof color === 'string' || !color) return (color || colors[colorName]) as HexColor;
  }

  return;
};

/**
 * Resolves a color to a HexColor if it starts with '#', otherwise attempts to get a Tailwind color hex.
 *
 * @param color - The color string to resolve.
 * @returns The resolved HexColor if the color starts with '#', otherwise the Tailwind color hex, or undefined.
 */
export const resolveColorProperty = (color?: string): HexColor | undefined => {
  if (!color) return;

  const lowerColor = color.toLowerCase();
  return lowerColor.startsWith('#') ? (lowerColor as HexColor) : getTailwindColor(lowerColor);
};

export const mapProductColor = (colorName: string) => {
  const productColorMap = {
    navy: 'blue-800',
    slate: 'gray-700',
    mint: 'green-300',
    beige: 'beige-100',
    champagne: 'beige',
    lilac: 'lilac-300',
  };

  return productColorMap[colorName as keyof typeof productColorMap] || colorName;
};

/**
 * Darkens the RGB values of a color by a specified amount.
 *
 * @param {Array<number>} rgb - An array of RGB values representing a color.
 * @param {number} amt - The amount to darken the color by. Must be a positive number.
 * Number represents percentage of black. E.g. 0.1 - 10% black
 * @return {Array<number>} The RGB values of the darkened color.
 */
function darkenRGB(rgb: Array<number>, amt: number): Array<number> {
  return rgb.map((val) => Math.max(0, val - amt));
}

/**
 * Converts an RGB color value to a hexadecimal color string.
 *
 * @param rgb - An array of three numbers representing the red, green, and blue components of the color.
 * @returns A string representing the hexadecimal color value.
 */
function rgbToHex(rgb: Array<number>) {
  return ('#' +
    rgb
      .map((val) => {
        const hex = val.toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      })
      .join('')) as HexColor;
}

/**
 * Calculates a darker shade of a given hex color by mixing it with black.
 *
 * @param colorHex - The original color in hex format.
 * @param blackPercentage - The percentage of black to mix with the original color (0 to 1).
 * @returns The darker color in hex format.
 */
export const calculateDarkerHex = (colorHex: HexColor, blackPercentage: number): HexColor => {
  const darkerColorRGB = darkenRGB(hexToRgbArray(colorHex), Math.floor(255 * blackPercentage));
  return rgbToHex(darkerColorRGB);
};

/**
 * Generates an option object with a key, title, and value based on the provided color hex code.
 *
 * @param colorHex - The hex color code as a string. It can be undefined.
 * @param colorHexMappings - An array of color hex mappings to find the corresponding title.
 * @param defaultTitle - An optional default title to use if no matching color hex mapping is found.
 * @returns An option object containing the key, title, and value based on the color hex code.
 */
export const getTitledHexColorOption = (
  colorHex: string | undefined,
  colorHexMappings: ColorHexMapping[],
  defaultTitle?: string
) => {
  const colorHexRefined = colorHex?.replace(/[# ]/g, '');
  if (!colorHexRefined) return;

  const matchedColorHexMapping = colorHexMappings.find(
    (colorHexMapping) =>
      colorHexMapping.value?.value?.toString().toUpperCase() === colorHexRefined.toUpperCase()
  );
  const colorHexLabel = matchedColorHexMapping
    ? getSharedDictionary(matchedColorHexMapping, 'title')?.value?.toString() || defaultTitle
    : defaultTitle;

  return { key: colorHexRefined, title: colorHexLabel, value: colorHexRefined };
};

/**
 * Determines the contrast theme class based on background color brightness.
 * For gradients or unsupported color formats, defaults to 'light'.
 * @param backgroundColor - The background color to evaluate (hex, Tailwind color, or undefined)
 * @returns 'light' for bright/unsupported colors, 'dark' for dark colors
 */
export const getContrastThemeClass = (backgroundColor: string | undefined): 'light' | 'dark' => {
  if (!backgroundColor) return 'light';
  if (backgroundColor.includes('gradient')) return 'light';
  const resolvedColor = resolveColorProperty(backgroundColor);
  return resolvedColor && !isColorBright(resolvedColor) ? 'dark' : 'light';
};

/**
 * Resolves a color or gradient value to a React CSS background style.
 *
 * @param value - The color or gradient value to resolve
 * @returns React CSS properties object with background or backgroundColor, or undefined if invalid
 */
export const resolveBackgroundStyle = (
  colorOrGradient: string | undefined
): React.CSSProperties | undefined => {
  if (!colorOrGradient) return undefined;

  const lowerValue = colorOrGradient.toLowerCase();

  if (lowerValue.includes('linear-gradient')) {
    return { background: colorOrGradient };
  }

  const color = resolveColorProperty(lowerValue);
  return color ? { backgroundColor: color } : undefined;
};
